import React, { useState, useRef } from "react";

import { Loop } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Access, Options } from "../../../models/access";
import { Order } from "../../../models/order";
import { Sales as SalesQuote, CustomerLocations } from "../../../models/sales";
import { onConfirmRemoveAddress, onRemoveAddress } from "../../../util/actions";
import Button, { Color } from "../../Form/Button";
import AddressInfoForm from "../AddressComponent/AddressInfoForm";
import RemoveItemsFromLocationModal, {
  RemoveItemsFromLocationHandles,
} from "../RemoveItemsFromLocationModal";
import AccessInfoForm from "./AccessInfoForm";
import useStyles from "./styles";

type TOptions = {
  drivewayLength: Options[];
  parkings: Options[];
  carryDistance: Options[];
  accessActions: Options[];
  drivewayList: Options[];
};
interface Props {
  data?: SalesQuote | Order;
  selectOptions: TOptions;
  handleUpdate: (attribute: string, value: any) => void;
  errors?: yup.ValidationError[];
  viewMode?: boolean;
}

const AccessComponent = ({ data, selectOptions, handleUpdate, errors, viewMode }: Props) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const removeItemsRef = useRef<RemoveItemsFromLocationHandles>(null);

  const [isPickupAccess, setIsPickupAccess] = useState<boolean>(true);
  const [isExpanded, setIsExpanded] = useState<boolean>(true);

  return (
    <>
      <div className={`d-flex align-items-center flex-row ${classes.containerTitle}`}>
        <span data-cy="accessComponentTitle" className={classes.accessTitle}>
          {isPickupAccess ? translate("pickupAccess") : translate("deliveryAccess")}
        </span>
        <Button
          data-cy="accessComponentSwitchButton"
          startIcon={<Loop />}
          onClick={() => setIsPickupAccess(!isPickupAccess)}
          customColor={Color.SECONDARY}
        >
          {isPickupAccess ? translate("switchToDeliveryAccess") : translate("switchToPickupAccess")}
        </Button>
      </div>
      <div className="mt-1">
        <AddressInfoForm
          tab="access"
          isExpandedAddress={isExpanded}
          setIsExpandedAddress={setIsExpanded}
          view={viewMode}
          condition={isPickupAccess ? translate("pickup") : translate("delivery")}
          addressesArray={
            isPickupAccess ? data?.origin?.locations || [] : data?.destination?.locations || []
          }
          update={(value) => {
            handleUpdate(isPickupAccess ? "origin" : "destination", {
              customer: isPickupAccess ? data?.origin?.customer : data?.destination?.customer,
              locations: value,
            });
          }}
          onRemoveAddress={(from: string, locationIndex: number) =>
            onRemoveAddress({
              from,
              locationIndex,
              customerLocation: isPickupAccess
                ? data?.origin || ({} as CustomerLocations)
                : data?.destination || ({} as CustomerLocations),
              modalReference: removeItemsRef,
              inventory: data?.inventory || [],
              updateAddress: (locations: CustomerLocations) =>
                handleUpdate(isPickupAccess ? "origin" : "destination", locations),
            })
          }
        />
      </div>
      {isPickupAccess ? (
        <AccessInfoForm
          data={data?.pickupAccess || ({} as Access)}
          selectOptions={selectOptions}
          update={(value: Access) => handleUpdate("pickupAccess", value)}
          errors={errors}
          described="pickupAccess"
          viewMode={viewMode}
        />
      ) : (
        <AccessInfoForm
          data={data?.deliveryAccess || ({} as Access)}
          selectOptions={selectOptions}
          update={(value: Access) => handleUpdate("deliveryAccess", value)}
          errors={errors}
          described="deliveryAccess"
          viewMode={viewMode}
        />
      )}

      <RemoveItemsFromLocationModal
        ref={removeItemsRef}
        onAccept={(locationId: string | number) =>
          onConfirmRemoveAddress({
            addressId: locationId,
            customerDestinationLocation: data?.destination || ({} as CustomerLocations),
            inventory: data?.inventory || [],
            updateAddress: (inventory, customerLocation) =>
              handleUpdate("all", data && { ...data, inventory, destination: customerLocation }),
          })
        }
        onDecline={() => {}}
      />
    </>
  );
};

export default AccessComponent;
